import { z } from 'zod';

/**
 * File Upload Configuration
 * Centralized limits for file uploads
 */
export const FILE_UPLOAD_CONFIG = {
  MAX_FILE_SIZE: 250 * 1024 * 1024, // 250MB — non-image files stream to storage, never buffered whole
  MAX_IMAGE_FILE_SIZE: 20 * 1024 * 1024, // 20MB in bytes — images don't need 50MB
  MAX_FILES_PER_UPLOAD: 1, // Single file upload
} as const;

/**
 * File Status Enum Schema
 * Matches Prisma FileStatus enum
 */
export const FileStatusSchema = z.enum(['ACTIVE', 'PROCESSING', 'FAILED', 'DELETED']);

/**
 * Image Version Schema
 * Single version of an image (original, low-res, or preview)
 */
export const ImageVersionSchema = z.object({
  storageKey: z.string(),
  url: z.string().url(),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  size: z.number().int().positive(),
  format: z.string().optional(),
});

/**
 * Image Versions Schema
 * All versions of an image file
 */
export const ImageVersionsSchema = z.object({
  // Original: Full resolution, unchanged
  original: ImageVersionSchema,

  // Low-res: Max 1024px width, maintain aspect ratio (for zoom-out rendering)
  lowRes: ImageVersionSchema,

  // Preview: 1024x576 center-crop (16:9 ratio, for node item thumbnails)
  preview: ImageVersionSchema,
});

/**
 * File Metadata Schema
 * Additional metadata stored in JSON field
 */
export const FileMetadataSchema = z.object({
  // Image metadata (original dimensions)
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  aspectRatio: z.number().positive().optional(),
  format: z.string().optional(),
  space: z.string().optional(),
  channels: z.number().int().positive().optional(),
  hasAlpha: z.boolean().optional(),

  // Video metadata
  duration: z.number().positive().optional(),
  fps: z.number().positive().optional(),
  codec: z.string().optional(),

  // Audio metadata
  bitrate: z.number().int().positive().optional(),
  sampleRate: z.number().int().positive().optional(),

  // Image versions (NEW: for multi-resolution support)
  versions: ImageVersionsSchema.optional(),

  // AI generation metadata (NEW)
  aiGenerated: z.boolean().optional(),
  originalUrl: z.string().url().optional(), // AI service URL (for reference)

  // Processing info
  thumbnailUrl: z.string().url().optional(), // DEPRECATED: Use versions.preview instead
  thumbnail: z.object({
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    storageKey: z.string(),
    url: z.string().url().optional(),
  }).optional(), // DEPRECATED: Use versions.preview instead
  optimizedUrl: z.string().url().optional(),
  processingStartedAt: z.string().datetime().optional(),
  processingCompletedAt: z.string().datetime().optional(),
  processingError: z.string().optional(),

  // Custom metadata
  tags: z.array(z.string()).optional(),
  description: z.string().optional(),
}); // Strip unknown fields for security (Zod default)

/**
 * Canvas File Creation Schema
 * Used when uploading a new file
 */
export const CanvasFileCreateSchema = z.object({
  filename: z
    .string()
    .min(1, { message: 'Filename is required' })
    .max(255, { message: 'Filename must be less than 255 characters' }),

  contentType: z
    .string()
    .regex(/^[a-z]+\/[a-z0-9\-+.]+$/i, { message: 'Invalid content type' }),

  size: z
    .number()
    .int()
    .positive({ message: 'File size must be positive' })
    .max(FILE_UPLOAD_CONFIG.MAX_FILE_SIZE, {
      message: `File size must be less than ${FILE_UPLOAD_CONFIG.MAX_FILE_SIZE / (1024 * 1024)}MB`
    }),

  nodeId: z
    .string()
    .optional(),

  metadata: FileMetadataSchema.optional().default({}),
});

/**
 * Canvas File Update Schema
 * Used for updating file metadata
 */
export const CanvasFileUpdateSchema = z.object({
  filename: z
    .string()
    .min(1)
    .max(255)
    .optional(),

  nodeId: z
    .string()
    .nullable()
    .optional(), // Allow null to remove node association

  folderId: z
    .string()
    .nullable()
    .optional(), // Move file to folder or remove from folder (null)

  metadata: FileMetadataSchema.optional(),

  status: FileStatusSchema.optional(),
});

/**
 * Canvas File Response Schema
 * Used for API responses
 */
export const CanvasFileResponseSchema = z.object({
  uuid: z.string().uuid(),
  filename: z.string(),
  storageKey: z.string(),
  contentType: z.string(),
  size: z.number().int(),
  publicUrl: z.string().url().nullable(),
  canvasId: z.string(),
  userId: z.string(),
  nodeId: z.string().nullable(),
  metadata: FileMetadataSchema,
  status: FileStatusSchema,
  deletedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

/**
 * Canvas File List Query Schema
 * Used for filtering and pagination
 */
export const CanvasFileListQuerySchema = z.object({
  canvasId: z.string().optional(),
  nodeId: z.string().optional(),
  status: FileStatusSchema.optional(),
  contentType: z.string().optional(),

  // Pagination
  limit: z
    .number()
    .int()
    .positive()
    .max(100)
    .default(50),

  offset: z
    .number()
    .int()
    .min(0)
    .default(0),

  // Sorting
  sortBy: z
    .enum(['createdAt', 'updatedAt', 'filename', 'size'])
    .default('createdAt'),

  sortOrder: z
    .enum(['asc', 'desc'])
    .default('desc'),
});

/**
 * File Upload Schema
 * Used for multipart file upload validation
 */
export const FileUploadSchema = z.object({
  file: z.instanceof(File, { message: 'File is required' }),
  nodeId: z.string().optional(),
});

// TypeScript types inferred from schemas
export type ImageVersion = z.infer<typeof ImageVersionSchema>;
export type ImageVersions = z.infer<typeof ImageVersionsSchema>;
export type FileStatus = z.infer<typeof FileStatusSchema>;
export type FileMetadata = z.infer<typeof FileMetadataSchema>;
export type CanvasFileCreateInput = z.infer<typeof CanvasFileCreateSchema>;
export type CanvasFileUpdateInput = z.infer<typeof CanvasFileUpdateSchema>;
export type CanvasFileResponse = z.infer<typeof CanvasFileResponseSchema>;
export type CanvasFileListQuery = z.infer<typeof CanvasFileListQuerySchema>;
export type FileUploadInput = z.infer<typeof FileUploadSchema>;

// Database model type (matches Prisma model)
export type CanvasFileModel = {
  id: string;
  uuid: string;
  filename: string;
  storageKey: string;
  contentType: string;
  size: number;
  publicUrl: string | null;
  canvasId: string;
  userId: string;
  nodeId: string | null;
  metadata: FileMetadata;
  status: FileStatus;
  deletedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
};
